import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from 'reactstrap';
import Pagination from './Pagination';

const ActionsDisplay = ({ statusFilter = 'open', orderFilter = 'oldest', onViewAction, filter = 'my' }) => {
  const [actions, setActions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  const fetchActions = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 50,
        status: statusFilter,
        order: orderFilter,
        filter: filter, // 'my' or 'all'
      };
      console.log('[ActionsDisplay] Fetching actions with params:', params);
      const response = await axios.get(`${API_BASE_URL}/admin/actions`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      console.log('[ActionsDisplay] Received actions count:', response.data.data?.length || 0, 'Filter:', filter);
      
      if (response.data.data && response.data.pagination) {
        setActions(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setActions(Array.isArray(response.data) ? response.data : []);
        setPagination(null);
      }
    } catch (err) {
      console.error('Failed to fetch actions:', err);
      setError('Failed to fetch actions');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchActions(page);
  };

  // Refetch when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchActions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, orderFilter, filter]);

  // Format event type for display
  const formatEventType = (eventType) => {
    return eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get title from metadata
  const getActionTitle = (action) => {
    if (action.metadata && action.metadata.title) {
      return action.metadata.title;
    }
    return `${formatEventType(action.eventType)} #${action.eventId.substring(0, 8)}`;
  };

  // Handle view button - trigger callback if provided, otherwise navigate
  const handleView = (action) => {
    if (onViewAction) {
      onViewAction(action);
    } else {
      // Fallback to navigation if no callback provided
      const baseUrl = window.location.origin;
      let url = '';

      switch (action.eventType) {
        case 'USER_SIGNUP':
          url = `${baseUrl}/users`;
          break;
        case 'PRODUCT_LISTING':
          url = `${baseUrl}/products`;
          break;
        case 'ISSUE':
          url = `${baseUrl}/issues`;
          break;
        case 'LISTING_REPORT':
          url = `${baseUrl}/reported-listings`;
          break;
        case 'USER_REPORT':
          url = `${baseUrl}/reported-users`;
          break;
        default:
          url = `${baseUrl}/dashboard`;
      }

      window.location.href = url;
    }
  };

  // Format SLA badge
  const getSLABadge = (action) => {
    if (action.status === 'CLOSED') {
      return (
        <span style={{
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: '#6c757d',
          color: 'white',
          fontSize: '12px'
        }}>
          Closed
        </span>
      );
    }

    if (action.hoursRemaining === null) {
      return (
        <span style={{
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: '#6c757d',
          color: 'white',
          fontSize: '12px'
        }}>
          No SLA
        </span>
      );
    }

    const isOverdue = action.isOverdue || action.hoursRemaining < 0;
    const hoursText = action.hoursRemaining < 0 
      ? `${Math.abs(action.hoursRemaining)}h overdue`
      : `${action.hoursRemaining}h remaining`;

    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: isOverdue ? '#dc3545' : (action.hoursRemaining < 12 ? '#ffc107' : '#28a745'),
        color: 'white',
        fontSize: '12px',
        fontWeight: isOverdue ? 'bold' : 'normal'
      }}>
        {hoursText}
      </span>
    );
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  const showClosedAt = statusFilter === 'all';
  const showAssignee = filter === 'all'; // Only show assignee column when viewing all actions

  return (
    <div>
      <table style={{ width: '100%', marginTop: '20px', borderCollapse: 'collapse' }}>
        <thead style={{
          backgroundColor: 'rgb(250, 248, 255)',
          borderTop: '1px solid rgb(233, 230, 241)',
          borderBottom: '1px solid rgb(233, 230, 241)',
        }}>
          <tr className='link-row border-bottom'>
            <th className="p-3 text-center">Event Type</th>
            <th className="p-3 text-center">Title</th>
            <th className="p-3 text-center">SLA Status</th>
            <th className="p-3 text-center">Status</th>
            {showAssignee && <th className="p-3 text-center">Assigned To</th>}
            <th className="p-3 text-center">Created</th>
            {showClosedAt && <th className="p-3 text-center">Closed</th>}
            <th className="p-3 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {actions.length === 0 ? (
            <tr>
              <td colSpan={showClosedAt && showAssignee ? 8 : showClosedAt || showAssignee ? 7 : 6} className="text-center">No actions found</td>
            </tr>
          ) : (
            actions.map(action => {
              const isClosed = action.status === 'CLOSED';
              const rowStyle = isClosed ? { 
                opacity: 0.6, 
                backgroundColor: '#f5f5f5' 
              } : {};

              return (
                <tr key={action.id} className="link-row border-bottom" style={rowStyle}>
                  <td className="p-2 text-center">{formatEventType(action.eventType)}</td>
                  <td className="p-2 text-center">{getActionTitle(action)}</td>
                  <td className="p-2 text-center">{getSLABadge(action)}</td>
                  <td className="p-2 text-center">
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      backgroundColor: action.status === 'OPEN' ? '#007bff' : '#6c757d',
                      color: 'white',
                      fontSize: '12px'
                    }}>
                      {action.status}
                    </span>
                  </td>
                  {showAssignee && (
                    <td className="p-2 text-center">
                      {action.assignee ? (
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: '#e9ecef',
                          color: '#495057',
                          fontSize: '12px'
                        }}>
                          {action.assignee.fullName || action.assignee.email || 'Unassigned'}
                        </span>
                      ) : (
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: '#f8f9fa',
                          color: '#6c757d',
                          fontSize: '12px'
                        }}>
                          Unassigned
                        </span>
                      )}
                    </td>
                  )}
                  <td className="p-2 text-center">
                    {new Date(action.createdAt).toLocaleDateString()} {new Date(action.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  {showClosedAt && (
                    <td className="p-2 text-center">
                      {action.closedAt 
                        ? `${new Date(action.closedAt).toLocaleDateString()} ${new Date(action.closedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                        : '-'
                      }
                    </td>
                  )}
                  <td className="p-2 text-center">
                    <Button 
                      size="sm" 
                      color="info" 
                      outline 
                      onClick={() => handleView(action)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {pagination && (
        <Pagination pagination={pagination} onPageChange={handlePageChange} />
      )}
    </div>
  );
};

export default ActionsDisplay;
